import { type Map as MaplibreMap } from "maplibre-gl";

import { COLOR_KEYS, COLORS } from "@/constants/colors";
import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import { generateColorRangesUniqueStops } from "../common";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";
import { buildStepColorExpression } from "./stepExpression";

export const addGridOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const colorRanges = style.colorRanges as { value: string; color: string }[];

  // Construct the match expression with unique, sorted values
  // const matchExpression =;

  // Add fill layer
  map.addLayer({
    id: dataset.sublayers.fill,
    type: "fill",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        ["has", category],
        [
          "match",
          ["get", category],
          colorRanges[0].value ?? "",
          COLORS[style.colorFamily][COLOR_KEYS[0]],
          ...(colorRanges
            .slice(1)
            ?.flatMap((range, index) => [
              range.value ?? "",
              COLORS[style.colorFamily][COLOR_KEYS[index + 1]]
            ]) ?? []),
          COLORS[style.colorFamily][COLOR_KEYS[colorRanges.length - 1]]
        ],
        "#CECECE"
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity ?? 0.7
      ]
    }
  });

  // Base outline — hidden on selected so neighbor white edges don't overpaint selection
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#ffffff",
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        0,
        1
      ]
    }
  });

  // Selection outline — separate layer drawn on top; render order guarantees no neighbor overpaint
  map.addLayer({
    id: dataset.sublayers.outlineSelected,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#FFCF01",
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};

export const addGridQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const uniqueRanges = generateColorRangesUniqueStops(style.colorRanges);

  // Ensure we have at least two ranges for a proper step expression
  // If we have 0 or 1 range, we can't create a valid step expression
  if (uniqueRanges.length < 2) {
    // If no ranges or just one range, use a default or the single color
    const fillColor =
      uniqueRanges.length === 1
        ? uniqueRanges[0][1]
        : COLORS[style.colorFamily][COLOR_KEYS[0]];

    map.addLayer({
      id: dataset.sublayers.fill,
      type: "fill",
      source: dataset.id,
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "fill-color": [
          "case",
          ["has", category],
          fillColor,
          "#CECECE" // Default fallback color
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          1,
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          style?.fillOpacity || 0.7
        ]
      }
    });
  } else {
    const stepColorExpression = buildStepColorExpression(
      uniqueRanges,
      category
    );

    map.addLayer({
      id: dataset.sublayers.fill,
      type: "fill",
      source: dataset.id,
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "fill-color": [
          "case",
          /* 1️⃣ property missing entirely → white */
          ["!", ["has", category]],
          "#FFFFFF",
          /* 2️⃣ category is null → grey */
          ["==", ["get", category], null],
          "#CECECE",
          /* 3️⃣ property exists and is not null → use step scale or constant color */
          stepColorExpression
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          1,
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          style?.fillOpacity || 0.7
        ]
      }
    });
  }

  // Base outline — hidden on selected so neighbor white edges don't overpaint selection
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#ffffff",
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        0,
        1
      ]
    }
  });

  // Selection outline — separate layer drawn on top; render order guarantees no neighbor overpaint
  map.addLayer({
    id: dataset.sublayers.outlineSelected,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": "#FFCF01",
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};
